import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Check, Sparkles, AlertTriangle, ArrowRight } from "lucide-react";

/**
 * Deep-link landing that a push notification opens:
 *   /nudge-ack/:token
 *
 * · Auto-acks on mount via /api/continuity-nudge/ack-by-token
 *   (HMAC-verified — no login required).
 * · Shows a clear success state so the HOD knows the nudge is silenced
 *   for the rest of the day.
 * · Failures (expired / tampered) surface a friendly explanation.
 */
export default function NudgeAckLanding() {
  const { token } = useParams();
  const [state, setState] = useState({ status: "pending", detail: null });

  useEffect(() => {
    if (!token) {
      setState({ status: "error", detail: "Missing token" });
      return;
    }
    api.post("/continuity-nudge/ack-by-token", { token })
       .then(({ data }) => setState({ status: "ok", detail: data }))
       .catch((err) =>
         setState({ status: "error",
                    detail: formatApiErrorDetail(err.response?.data?.detail)
                            || "This reminder link is invalid or expired." }));
  }, [token]);

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center px-6"
         data-testid="nudge-ack-landing">
      <div className="w-full max-w-md rounded-3xl border border-white/10 bg-white/[0.04] p-8">
        <div className="flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-[color:var(--association-theme)]">
          <Sparkles size={12} /> Continuity Reminder
        </div>

        {state.status === "pending" && (
          <div className="mt-6" data-testid="nudge-ack-pending">
            <h1 className="font-display text-3xl uppercase tracking-tight">
              Silencing today&apos;s nudge…
            </h1>
            <p className="text-sm text-white/60 mt-3">
              Confirming your acknowledgment.
            </p>
          </div>
        )}

        {state.status === "ok" && (
          <div className="mt-6" data-testid="nudge-ack-ok">
            <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-emerald-500/15 border border-emerald-400/40 mb-5">
              <Check size={26} className="text-emerald-300" />
            </div>
            <h1 className="font-display text-3xl uppercase tracking-tight">
              Nudge silenced
            </h1>
            <p className="text-sm text-white/70 mt-3 leading-relaxed">
              You&apos;re acknowledged as <b>logged</b> for
              {" "}<span className="text-white">{state.detail?.project_title || "this project"}</span> on
              {" "}<span className="text-white">{state.detail?.date}</span>. The
              reminder won&apos;t re-ping you today.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/projects"
                    className="inline-flex items-center gap-1.5 rounded-full bg-white text-black text-[10px] tracking-[0.22em] uppercase px-4 py-2 hover:opacity-90 transition-opacity"
                    data-testid="nudge-ack-projects">
                Go to projects <ArrowRight size={11} />
              </Link>
              <Link to="/my/continuity"
                    className="inline-flex items-center gap-1.5 rounded-full border border-white/20 hover:border-white/40 text-[10px] tracking-[0.22em] uppercase px-4 py-2 transition-colors"
                    data-testid="nudge-ack-continuity">
                Log another look <ArrowRight size={11} />
              </Link>
            </div>
          </div>
        )}

        {state.status === "error" && (
          <div className="mt-6" data-testid="nudge-ack-error">
            <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-amber-500/10 border border-amber-400/30 mb-5">
              <AlertTriangle size={26} className="text-amber-300" />
            </div>
            <h1 className="font-display text-3xl uppercase tracking-tight">
              Link can&apos;t open
            </h1>
            <p className="text-sm text-white/70 mt-3 leading-relaxed">
              {typeof state.detail === "string" ? state.detail
                : "This reminder link is invalid or expired."}
              {" "}You can still acknowledge from the app.
            </p>
            <Link to="/projects"
                  className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-white text-black text-[10px] tracking-[0.22em] uppercase px-4 py-2 hover:opacity-90 transition-opacity"
                  data-testid="nudge-ack-error-projects">
              Open the app <ArrowRight size={11} />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
